import React from "react";

import { PathResolver, withResolvePaths } from "../../../withResolvePaths";
import { FilterSourceInfoType, getFilterBySourceId, MultiLineFilter } from "../../MultiLine/MultiLineFilter";
import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { useTypedDispatch } from "../../../../../../Common/UseTypedSelector";
import { useMultilineClient } from "../../../../Engine/Core/Filtering/UseMultilineClient";
import { createHocDisplayName } from "../../../TypingUtils";

import { FilterDateRange } from "./FilterDateRange";

interface FilterDateRangeContainerProps {
    multilinePath: AbsoluteResolvedModelPath;
    dataPath: AbsoluteModelPath;
    resolvePath: PathResolver;
    filteredUniqKey?: string;
    convertDateToTicksInFilterExpression?: boolean;
}

const FilterDateRangeContainerWrapper: React.FC<FilterDateRangeContainerProps> = ({
    multilinePath,
    dataPath,
    resolvePath,
    filteredUniqKey = "default",
    convertDateToTicksInFilterExpression,
}) => {
    const dispatch = useTypedDispatch();

    const resolvedMultilinePath = resolvePath(multilinePath);
    const resolvedDataPath = resolvePath(dataPath);
    const { client, filters } = useMultilineClient(resolvedMultilinePath, filteredUniqKey, info => ({
        client: info.client,
        filters: info.multilineState.filters,
    }));
    const currentFilter = getFilterBySourceId(filters, resolvePath(dataPath).toLegacyPath());
    const currentFilterInfo =
        currentFilter?.filterSourceInfo?.type === FilterSourceInfoType.DateRange
            ? currentFilter?.filterSourceInfo
            : undefined;
    const startDate = currentFilterInfo?.startDate ?? "";
    const endDate = currentFilterInfo?.endDate ?? "";

    const onChangeFilter = React.useCallback(
        (_stickyTablePath: string, multilineFilter: MultiLineFilter) => client.setFilter(multilineFilter),
        [dispatch, resolvedMultilinePath, filteredUniqKey]
    );

    const onRemoveFilter = React.useCallback(
        (_stickyTablePath: string, filterSourceId: string) => client.removeFilter(filterSourceId),
        [client]
    );

    return (
        <FilterDateRange
            startDate={startDate}
            endDate={endDate}
            dataPath={resolvedDataPath}
            onChangeFilter={onChangeFilter}
            onRemoveFilter={onRemoveFilter}
            convertDateToTicksInFilterExpression={convertDateToTicksInFilterExpression}
        />
    );
};
FilterDateRangeContainerWrapper.displayName = createHocDisplayName(FilterDateRange, "FilterDateRangeContainerWrapper");

export const FilterDateRangeContainer = withResolvePaths(FilterDateRangeContainerWrapper);
